import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useEffect, useRef, useState } from 'react';
import { View, ActivityIndicator, StyleSheet, Text, Platform } from 'react-native';
import { supabase } from '@/utils/supabase';
import { isAdmin, COLORS, TYPOGRAPHY, SPACING } from '@/utils/adminUtils';

export default function AdminLayout() {
  const router = useRouter();
  const [booting, setBooting] = useState(true);

  const mountedRef = useRef(true);
  const navigatingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const safeSetBooting = (v: boolean) => {
      if (mountedRef.current) setBooting(v);
    };

    const safeReplace = (path: string) => {
      if (navigatingRef.current) return;
      navigatingRef.current = true;
      router.replace(path as any);
      // allow future navigation after this tick
      setTimeout(() => {
        navigatingRef.current = false;
      }, 0);
    };

    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const session = data.session;

        if (!session?.user?.id) {
          safeReplace('/admin/login');
          safeSetBooting(false);
          return;
        }

        const ok = await isAdmin(session.user.id);
        if (!ok) {
          await supabase.auth.signOut();
          safeReplace('/admin/login');
          safeSetBooting(false);
          return;
        }

        safeSetBooting(false);

        const { data: sub } = supabase.auth.onAuthStateChange(async (_event, s) => {
          if (!mountedRef.current) return;

          if (!s?.user?.id) {
            safeReplace('/admin/login');
            return;
          }

          const stillAdmin = await isAdmin(s.user.id);
          if (!stillAdmin) {
            await supabase.auth.signOut();
            safeReplace('/admin/login');
          }
        });

        subscription = sub.subscription;
      } catch (e) {
        console.error('[AdminLayout] init error:', e);
        safeReplace('/admin/login');
        safeSetBooting(false);
      }
    };

    init();

    return () => {
      mountedRef.current = false;
      subscription?.unsubscribe();
    };
  }, [router]);

  if (booting) {
    return (
      <View style={styles.loading} accessibilityRole="progressbar" accessibilityLabel="בודק הרשאות">
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>בודק הרשאות...</Text>
      </View>
    );
  }

  return (
    <>
      {Platform.OS === 'web' && (
        <Head>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
      )}
      <Stack screenOptions={{ headerShown: false }}>
        <Stack.Screen name="login" />
        <Stack.Screen name="index" />
        <Stack.Screen name="dashboard" />
        <Stack.Screen name="export" />
      </Stack>
    </>
  );
}

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.bg,
    gap: SPACING.lg,
  },
  loadingText: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
  },
});
